import { MouseEvent, useCallback } from "react";

import { EmptyArray } from "../../utilities/EmptyArray";
import { Checkbox, CheckboxValue } from "../../forms/Checkbox";
import { DataId } from "../DataId";
import classNames from "classnames";

import { DataRow } from "../DataRow";
import { BaseColumnProps } from "./BaseColumnProps";
import { DisabledRowCheckHandler } from "./DisabledRowCheckHandler";
import { useTableStyles } from "./TableStylesContext";
import { Custom } from "./columnTypes/Custom";
import { useIsChildRow } from "./useIsChildRow";

/**
 * The `Text` component properties.
 */

export interface SelectionColumnProps<TRow extends object> extends BaseColumnProps {
  /**
   * The set of selected row IDs.
   */
  readonly selectedIds?: ReadonlyArray<DataId>;

  /**
   * The set of selected IDs.
   */
  readonly selectedIdSet: ReadonlySet<DataId>;

  /**
   * Function to call when the set of selected row IDs are to be changed.
   */
  readonly onSelectedIdsChange?: (ids: ReadonlyArray<DataId>, previousIds: ReadonlyArray<DataId>) => void;

  /**
   * Function to call when the row is to be tested if it should have a disabled checkbox
   */
  readonly onCheckDisabledRow?: DisabledRowCheckHandler<TRow>;
}

/** The `SelectionColumnContent` component properties. */
interface SelectionColumnContentProps {
  /** The row ID. */
  readonly rowId: DataId;

  /** Indicates whether selected. */
  readonly selected: boolean;

  /** Indicates whether read-only. */
  readonly readOnly: boolean;

  /** Called when the selection state is being changed. */
  readonly onChange?: (rowId: DataId, selected: boolean) => void;
}

/** The selection column content. */
const SelectionColumnContent = (props: SelectionColumnContentProps) => {
  const { rowId, selected, readOnly, onChange } = props;

  const isChildRow = useIsChildRow();
  const { styles } = useTableStyles();

  const clickHandler = useCallback((ev: MouseEvent) => {
    ev.stopPropagation();
  }, []);

  const selectionChangeHandler = useCallback(
    (value: CheckboxValue) => {
      if (value === null) value = false;
      if (typeof value === "string") value = false;

      onChange?.(rowId, value);
    },
    [rowId, onChange],
  );

  if (isChildRow) return null;

  return (
    <div className={classNames(styles.CheckboxCellContainer, "checkbox-cell-container")} onClick={clickHandler}>
      <Checkbox
        value={selected}
        onChange={selectionChangeHandler}
        hideInstructions
        hideLabel
        defaultValue={selected}
        readOnly={readOnly}
        aria-label={`Select row ${rowId}`}
      />
    </div>
  );
};

/** Column which displays current row selection state. */
export const SelectionColumn = <TRow extends object>(props: SelectionColumnProps<TRow>) => {
  const { selectedIds = EmptyArray, selectedIdSet, onSelectedIdsChange, onCheckDisabledRow } = props;

  const selectionChangeHandler = useCallback(
    (rowId: DataId, selected: boolean) => {
      if (selected && !selectedIdSet.has(rowId)) onSelectedIdsChange?.([...selectedIds, rowId], selectedIds);
      else if (!selected && selectedIdSet.has(rowId))
        onSelectedIdsChange?.(
          selectedIds.filter((selectedId) => selectedId !== rowId),
          selectedIds,
        );
    },
    [selectedIds, selectedIdSet, onSelectedIdsChange],
  );

  const columnTemplateFunction = useCallback(
    (_: unknown, id: DataId, row: DataRow<object>) => (
      <SelectionColumnContent
        rowId={id}
        selected={selectedIdSet.has(id)}
        readOnly={onCheckDisabledRow?.(row as DataRow<TRow>) || false}
        onChange={selectionChangeHandler}
      />
    ),
    [selectedIds, onSelectedIdsChange, onCheckDisabledRow, selectedIdSet],
  );

  return <Custom {...props}>{columnTemplateFunction}</Custom>;
};

SelectionColumn.className = "selection";
